import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/shared/auth/authStore";
import { softphoneApi, type BreakStatusDto, type PhoneStatus, type SessionStatus } from "@/shared/api/softphoneApi";
import { AGENT_STATUS, CALL_STATE, REG_STATE, type StatusMeta } from "@/shared/ui/status";
import { isAvailableBreak } from "./softphoneController";
import { derivePhoneStatus, useSoftphoneStore, type CallInfo, type RegState } from "./softphoneStore";

/** Relógio que re-renderiza a cada `ms` (timers de chamada/pausa). */
export function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function formatClock(ts: number): string {
  return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/** Registro SIP → meta visual do design system (cor + ícone + rótulo). */
export const REG_META: Record<RegState, StatusMeta> = REG_STATE;

/** Status de sessão do agente → meta visual (Disponível / Em pausa / Offline). */
export const SESSION_META: Record<SessionStatus, StatusMeta> = {
  ONLINE: AGENT_STATUS.ONLINE,
  PAUSED: AGENT_STATUS.PAUSED,
  OFFLINE: AGENT_STATUS.OFFLINE,
};

/** Meta visual da chamada atual (tocando / chamando / em chamada / em espera). */
export function callStateMeta(call: Pick<CallInfo, "state" | "held">) {
  if (call.state === "ringing") return CALL_STATE.ringing;
  if (call.state === "dialing") return CALL_STATE.dialing;
  return call.held ? CALL_STATE.held : CALL_STATE.active;
}

export const SESSION_LABEL: Record<SessionStatus, string> = {
  ONLINE: "Disponível",
  PAUSED: "Em pausa",
  OFFLINE: "Offline",
};

export const PHONE_LABEL: Record<PhoneStatus, string> = {
  OFFLINE: "Aparelho offline",
  ONLINE: "Livre",
  RINGING: "Tocando",
  DIALING: "Discando",
  INUSE: "Em chamada",
};

export function usePhoneStatus(): PhoneStatus {
  return useSoftphoneStore((s) => derivePhoneStatus(s));
}

export type BreakOption = BreakStatusDto & { timeLimit?: number | null };

/**
 * Pausas do agente: `/breaks/findBreakByUser` (status/contagem/aprovação do dia). Para quem não tem
 * ROLE_USER (403) cai para a lista do ramal (`phoneByLogin.breaks`), sem controle de limite.
 * `timeLimit` (minutos) vem da lista do ramal.
 */
export function useAgentBreaks(enabled: boolean) {
  const phone = useSoftphoneStore((s) => s.phone);
  const hasUserRole = useAuthStore((s) => s.roles.includes("ROLE_USER"));
  const q = useQuery({
    queryKey: ["softphone", "breaks"],
    queryFn: () => softphoneApi.findBreakByUser(),
    enabled: enabled && !!phone && hasUserRole,
    refetchInterval: 60_000,
    staleTime: 15_000,
  });
  const fromPhone = phone?.breaks?.data ?? [];
  const limits = new Map(fromPhone.map((b) => [String(b.value ?? b.title), b.timeLimit ?? null]));
  const base: BreakOption[] =
    q.data && q.data.length
      ? q.data
      : fromPhone.map((b) => ({
          breakId: (b as { breakId?: number }).breakId ?? null,
          title: b.title,
          value: b.value ?? b.title,
          status: "ENABLED" as const,
          countPause: null,
          maxQuantity: null,
        }));
  const list = base
    .filter((b) => !isAvailableBreak(b))
    .map((b) => ({ ...b, timeLimit: limits.get(String(b.value ?? b.title)) ?? null }))
    .sort((a, b) => String(a.title ?? "").localeCompare(String(b.title ?? ""), "pt-BR"));
  return { breaks: list, loading: q.isLoading, error: q.error, refetch: q.refetch };
}
